/**
 * Slice 6 — Tab-title computation for a Table Explorer tab driven by the
 * FK Breadcrumb navigation tree.
 *
 * Patterns (per spec § Tab title):
 *   - Root table-node     → `<table>` (no chip-row shown).
 *   - Filtered table-node → `<table> (filtered)`.
 *   - Row-node            → `<table> #<pk>` or `<table> #<pk> — <displayLabel>`.
 *
 * Pure function; callers wire it into a `useEffect` that calls
 * `updateTabTitle(tabId, newTitle, false)` when the title changes.
 */

import type {
  NavigationNode,
  NavigationTree,
} from '../../../core/types/workspaceTypes';

function formatPk(
  pk: ReadonlyArray<{ readonly column: string; readonly value: unknown }>,
): string {
  if (pk.length === 0) return '';
  if (pk.length === 1) return String(pk[0].value);
  return pk.map((p) => String(p.value)).join(',');
}

function titleForNode(node: NavigationNode): string {
  if (node.kind === 'table') {
    const filtered = (node.filters?.length ?? 0) > 0;
    return filtered ? `${node.table} (filtered)` : node.table;
  }
  const pkStr = formatPk(node.pk);
  const head = pkStr ? `${node.table} #${pkStr}` : node.table;
  if (node.displayLabel && node.displayLabel.length > 0) {
    return `${head} — ${node.displayLabel}`;
  }
  return head;
}

/**
 * Compute the auto-title for a Table Explorer tab given its current
 * navigation tree. When there's no tree (or only the root), falls back to
 * the bare table name. Returns null when the inputs don't carry enough
 * information to produce a meaningful title.
 */
export function computeTableExplorerTabTitle(args: {
  readonly tree: NavigationTree | null;
  readonly fallbackTable: string;
}): string | null {
  const { tree, fallbackTable } = args;
  if (!tree) {
    return fallbackTable ? fallbackTable : null;
  }
  const active = tree.nodes[tree.activeNodeId];
  if (!active) {
    return fallbackTable ? fallbackTable : null;
  }
  return titleForNode(active);
}
